import { QueryFilters } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDeviceIdList } from '@/api/deviceIdList/getDeviceIdList';
import { applyDevicesListInvalidation } from '@/api/devices/getDevices';
import { useUserAccount } from '@/api/userAccount';
import { axios } from '@/common/axios';
import { queryClient } from '@/common/react-query';
import {
    DETECTIONS_QUERY_KEY,
    DEVICES_LIST_QUERY_KEY,
    DEVICE_CELLULAR_SIGNAL_HISTORY_QUERY_KEY,
    DEVICE_STATUS_HISTORY_QUERY_KEY,
    DEVICE_TRACK_HISTORY_QUERY_KEY,
    HISTORICAL_BAROMETRIC_PRESSURE_DATA_QUERY_KEY,
    HISTORICAL_SENSOR_DATA_QUERY_KEY,
    HISTORICAL_SURFACE_TEMPERATURE_DATA_QUERY_KEY,
    HISTORICAL_WAVE_DATA_QUERY_KEY,
    HISTORICAL_WIND_DATA_QUERY_KEY,
    OCEANS_WAVE_DATA_QUERY_KEY,
    PARTITION_DATA_HISTORY_QUERY_KEY,
    WAVELINK_URL,
    WavelinkQueryKeyPrefix,
    parseDateRangeQueryKey,
} from '@/config';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useGlobalStore } from '@/store';

export type WavelinkStatus = 'disconnected' | 'connecting' | 'connected' | 'registered';

export type WavelinkQueryKey = readonly unknown[];

export interface UseWavelinkResult {
    status: WavelinkStatus;
    isQueryCovered: (queryKey: WavelinkQueryKey) => boolean;
}

interface InvalidationMessage {
    type: 'invalidate';
    queryKeys: string[][];
    ts: number;
}

interface ConnectedMessage {
    type: 'connected';
    connectionId: string;
}

type SseMessage = InvalidationMessage | ConnectedMessage | { type: string };

type InvalidationHandler = {
    /**
     * The queries `invalidate` refetches, so a fetch already running can be recognised before another
     * invalidation starts.
     *
     * Anything wider than what `invalidate` touches keeps the coalescing below armed by fetches it never
     * waits on, and the follow-up then runs again for as long as those keep going.
     */
    inFlight: (queryKey: string[]) => QueryFilters;
    invalidate: (queryKey: string[]) => Promise<void>;
};

/**
 * Wait on the refetch already running rather than replacing it.
 *
 * `invalidateQueries` defaults to cancelling an in-flight fetch and starting another, which for a device
 * reporting faster than the endpoint answers means abandoning a nearly-complete request once per report
 * and never landing any of them. Joining the running one and queueing a single follow-up (see
 * `invalidateCoalesced`) delivers the same data with one request per burst.
 */
const JOIN_REFETCH_IN_FLIGHT = { cancelRefetch: false };

/**
 * Wavelink tells us a spotter reported, not which window changed. Every date-ranged query goes through
 * `useLiveSplitQuery`, which marks the query covering the live edge. So refetch just that one and leave
 * every settled and historical window cached — otherwise one sample forces a refetch of every multi-day
 * range for that spotter.
 *
 * Without a spotter there is nothing to narrow against and the whole prefix goes.
 */
const liveWindowFilters = ([prefix, spotterId]: string[]): QueryFilters => {
    if (!spotterId) {
        return { queryKey: [prefix] };
    }

    return {
        queryKey: [prefix],
        predicate: ({ queryKey }) => {
            if (queryKey[1] !== spotterId) {
                return false;
            }
            // the settled chunks of a split range end far enough in the past that late arrivals still
            // land in the live window, so that is the only entry to refetch. A key shape the shared
            // builder did not produce is a bug, and over-fetching beats charts going quietly stale.
            return parseDateRangeQueryKey(queryKey)?.isLiveWindow ?? true;
        },
    };
};

const invalidateLiveWindow: InvalidationHandler = {
    inFlight: liveWindowFilters,
    invalidate: (queryKey) => queryClient.invalidateQueries(liveWindowFilters(queryKey), JOIN_REFETCH_IN_FLIGHT),
};

/** Refetches the whole key, for a prefix none of the handlers below claims. */
const invalidateWholeKey: InvalidationHandler = {
    inFlight: (queryKey) => ({ queryKey }),
    invalidate: (queryKey) => queryClient.invalidateQueries({ queryKey }, JOIN_REFETCH_IN_FLIGHT),
};

const invalidationHandlers: Record<WavelinkQueryKeyPrefix, InvalidationHandler> = {
    [DEVICES_LIST_QUERY_KEY]: {
        // the patch below fetches one device outside react-query, and the list query is what it writes into
        inFlight: ([prefix]) => ({ queryKey: [prefix] }),
        invalidate: ([, spotterId]) => applyDevicesListInvalidation(spotterId),
    },

    [HISTORICAL_SENSOR_DATA_QUERY_KEY]: invalidateLiveWindow,
    [DETECTIONS_QUERY_KEY]: invalidateLiveWindow,
    [DEVICE_TRACK_HISTORY_QUERY_KEY]: invalidateLiveWindow,
    [DEVICE_STATUS_HISTORY_QUERY_KEY]: invalidateLiveWindow,
    [DEVICE_CELLULAR_SIGNAL_HISTORY_QUERY_KEY]: invalidateLiveWindow,
    [HISTORICAL_BAROMETRIC_PRESSURE_DATA_QUERY_KEY]: invalidateLiveWindow,
    [HISTORICAL_WIND_DATA_QUERY_KEY]: invalidateLiveWindow,
    [HISTORICAL_SURFACE_TEMPERATURE_DATA_QUERY_KEY]: invalidateLiveWindow,
    [HISTORICAL_WAVE_DATA_QUERY_KEY]: invalidateLiveWindow,
    [PARTITION_DATA_HISTORY_QUERY_KEY]: invalidateLiveWindow,
    [OCEANS_WAVE_DATA_QUERY_KEY]: invalidateLiveWindow,
};

const isHandledPrefix = (prefix: string): prefix is WavelinkQueryKeyPrefix => prefix in invalidationHandlers;

/**
 * The invalidation running for each query key, and whether a message arrived while it ran.
 *
 * Keyed by the wavelink query key, so a busy spotter never holds up another one.
 */
const invalidationsInFlight = new Map<string, { queued: boolean }>();

/**
 * Runs one invalidation at a time per query key, and one more afterwards if anything arrived during it.
 *
 * Every message means the same thing - this window may have gained data - so the messages that land while
 * a refetch is running collapse into the single follow-up that picks up whatever they were reporting. That
 * follow-up is what keeps the last message of a burst from being lost, since a query wavelink covers has
 * `refetchInterval` switched off and a push is the only thing that would fetch it again.
 */
const invalidateCoalesced = (queryKey: string[], handler: InvalidationHandler): void => {
    const cacheKey = JSON.stringify(queryKey);
    const inFlight = invalidationsInFlight.get(cacheKey);
    if (inFlight) {
        inFlight.queued = true;
        return;
    }

    /*
     * A matching refetch that started before this message may return stale data, so queue one follow-up.
     * Use the handler's filters to ignore unrelated in-flight queries, such as settled chunks.
     */
    const entry = { queued: queryClient.isFetching(handler.inFlight(queryKey)) > 0 };
    invalidationsInFlight.set(cacheKey, entry);

    handler
        .invalidate(queryKey)
        .catch(() => {
            // a refetch that failed leaves the query holding its own error, and the next message retries
        })
        .finally(() => {
            invalidationsInFlight.delete(cacheKey);
            if (entry.queued) {
                invalidateCoalesced(queryKey, handler);
            }
        });
};

function handleInvalidation(message: InvalidationMessage) {
    for (const queryKey of message.queryKeys) {
        const prefix = queryKey[0];
        invalidateCoalesced(queryKey, isHandledPrefix(prefix) ? invalidationHandlers[prefix] : invalidateWholeKey);
    }
}

const HEARTBEAT_INTERVAL_MS = 25_000;
const MISSED_HEARTBEATS_THRESHOLD = 3;
const MAX_RETRIES = 5;
const RETRY_DELAYS = [0, 2000, 10_000, 30_000, 60_000];

export function useWavelinkConnection(): UseWavelinkResult {
    const wavelinkEnabled = useFeatureFlag('wavelinkEnabled');
    const isLoggedIn = useGlobalStore((state) => state.isLoggedIn);
    const { data: accountData } = useUserAccount();
    const { data: deviceIdListData } = useDeviceIdList();

    const username = accountData?.data?.user?.username ?? null;
    const billableAccountId = accountData?.data?.user?.billableAccountId ?? null;
    const spotterIds = useMemo(
        () => deviceIdListData?.data?.devices?.map((d) => d.spotterId) ?? null,
        [deviceIdListData]
    );

    const [status, setStatus] = useState<WavelinkStatus>('disconnected');

    const eventSourceRef = useRef<EventSource | null>(null);
    const connectionIdRef = useRef<string | null>(null);
    const lastEventTimeRef = useRef<number>(0);
    const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const retryCountRef = useRef(0);
    const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const coveredQueryKeysRef = useRef<string[][] | null>(null);
    const mountedRef = useRef(true);

    // Keep latest values in refs so callbacks don't need to re-create
    const usernameRef = useRef(username);
    usernameRef.current = username;
    const billableAccountIdRef = useRef(billableAccountId);
    billableAccountIdRef.current = billableAccountId;
    const spotterIdsRef = useRef(spotterIds);
    spotterIdsRef.current = spotterIds;

    // All imperative functions stored in a single ref to break dependency cycles.
    // This ensures the main effect never re-runs due to callback identity changes.
    const fnRef = useRef({
        cleanup() {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }
            if (heartbeatTimerRef.current) {
                clearInterval(heartbeatTimerRef.current);
                heartbeatTimerRef.current = null;
            }
            connectionIdRef.current = null;
            coveredQueryKeysRef.current = null;
        },

        async register(connectionId: string, user: string, spotters: string[], billableAccId: number) {
            try {
                const { token } = await axios.get<{ token: string }, { token: string }>('/api/wavelink-token');

                const response = await fetch(`${WAVELINK_URL}/register-sse`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        connectionId,
                        username: user,
                        spotterIds: spotters,
                        token,
                        billableAccountId: billableAccId,
                    }),
                });

                if (!response.ok && response.status !== 409) {
                    throw new Error(`Registration failed: ${response.status}`);
                }

                const data = await response.json();
                coveredQueryKeysRef.current = data.coveredQueryKeys ?? null;
                retryCountRef.current = 0;
                if (mountedRef.current) {
                    setStatus('registered');
                }
            } catch {
                fnRef.current.cleanup();
                if (mountedRef.current) {
                    setStatus('disconnected');
                    fnRef.current.scheduleRetry();
                }
            }
        },

        scheduleRetry() {
            if (retryCountRef.current >= MAX_RETRIES) {
                return;
            }
            if (retryTimerRef.current) {
                clearTimeout(retryTimerRef.current);
                retryTimerRef.current = null;
            }
            const delay = RETRY_DELAYS[retryCountRef.current] ?? RETRY_DELAYS[RETRY_DELAYS.length - 1];
            retryCountRef.current++;
            retryTimerRef.current = setTimeout(() => {
                retryTimerRef.current = null;
                if (mountedRef.current) {
                    fnRef.current.connect();
                }
            }, delay);
        },

        /**
         * An open EventSource or an owed attempt both mean something is already in hand, and restarting either
         * would throw away a connection that is still coming up. Those refs answer the question rather than
         * `status`, because they are set synchronously and so are already true on the first render.
         */
        reconnectIfAbandoned() {
            if (document.visibilityState === 'hidden' || eventSourceRef.current || retryTimerRef.current) {
                return;
            }

            retryCountRef.current = 0;
            fnRef.current.connect();
        },

        connect() {
            if (!mountedRef.current) {
                return;
            }

            fnRef.current.cleanup();
            setStatus('connecting');

            const es = new EventSource(`${WAVELINK_URL}/events`);
            eventSourceRef.current = es;

            es.onmessage = (event) => {
                let parsed: SseMessage;
                try {
                    parsed = JSON.parse(event.data);
                } catch {
                    return;
                }

                /*
                 * Use the client's clock because the liveness check compares this value with `Date.now()`.
                 * Using the sender's `ts` would make clock skew affect the elapsed time.
                 */
                lastEventTimeRef.current = Date.now();

                if (parsed.type === 'connected') {
                    const { connectionId } = parsed as ConnectedMessage;
                    connectionIdRef.current = connectionId;
                    setStatus('connected');
                } else if (parsed.type === 'invalidate') {
                    handleInvalidation(parsed as InvalidationMessage);
                }
            };

            es.onerror = () => {
                if (!mountedRef.current) {
                    return;
                }
                fnRef.current.cleanup();
                setStatus('disconnected');
                fnRef.current.scheduleRetry();
            };

            // Start heartbeat monitor
            lastEventTimeRef.current = Date.now();
            heartbeatTimerRef.current = setInterval(() => {
                const elapsed = Date.now() - lastEventTimeRef.current;
                if (elapsed > HEARTBEAT_INTERVAL_MS * MISSED_HEARTBEATS_THRESHOLD) {
                    fnRef.current.cleanup();
                    if (mountedRef.current) {
                        setStatus('disconnected');
                        fnRef.current.scheduleRetry();
                    }
                }
            }, HEARTBEAT_INTERVAL_MS);
        },
    });

    // If we receive connected event but didn't have username/spotterIds yet,
    // register once they become available
    useEffect(() => {
        if (status === 'connected' && connectionIdRef.current && username && spotterIds && billableAccountId) {
            fnRef.current.register(connectionIdRef.current, username, spotterIds, billableAccountId);
        }
    }, [status, username, spotterIds, billableAccountId]);

    // Main connect effect — only depends on reactive data, not callback identities
    useEffect(() => {
        if (!wavelinkEnabled || !isLoggedIn || !username || !spotterIds) {
            return;
        }

        const fn = fnRef.current;
        fn.connect();

        document.addEventListener('visibilitychange', fn.reconnectIfAbandoned);

        return () => {
            document.removeEventListener('visibilitychange', fn.reconnectIfAbandoned);
            fn.cleanup();
            if (retryTimerRef.current) {
                clearTimeout(retryTimerRef.current);
                retryTimerRef.current = null;
            }
        };
    }, [wavelinkEnabled, isLoggedIn, username, spotterIds]);

    // Track mounted state
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const isQueryCovered = useCallback(
        (queryKey: WavelinkQueryKey): boolean => {
            if (status !== 'registered') {
                return false;
            }

            const coveredQueryKeys = coveredQueryKeysRef.current;
            if (!coveredQueryKeys?.length) {
                return false;
            }

            return coveredQueryKeys.some((coveredKey) => {
                return coveredKey.every((segment, idx) => queryKey[idx] === segment);
            });
        },
        [status]
    );

    return useMemo(
        () => ({
            status,
            isQueryCovered,
        }),
        [status, isQueryCovered]
    );
}

export const exportForTests = {
    // Drops every record of an invalidation in flight.
    resetInvalidationCoalescing: (): void => invalidationsInFlight.clear(),
};
